import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarIcon, PackageIcon, ReceiptIcon, UserIcon } from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import { Button } from '@/components/button/button';
import { Skeleton } from '@/components/skeleton/skeleton';
import { getListVoucherRedemptionQueryOptions } from '@/features/vouchers/api/get-list-voucher-redemption.api';
import type { VoucherRedemption } from '@/features/vouchers/types/voucher-redemption.type';

const LIMIT = 10;

const STATUS_LABEL: Record<
  string,
  { label: string; variant: 'warning' | 'success' | 'secondary' }
> = {
  RESERVED: { label: 'IN USE', variant: 'warning' },
  CONSUMED: { label: 'SUCCESS', variant: 'success' },
  RELEASED: { label: 'CANCEL', variant: 'secondary' },
};

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

export interface VoucherRedemptionHistoryProps {
  voucherId: string;
}

export default function VoucherRedemptionHistory({
  voucherId,
}: VoucherRedemptionHistoryProps) {
  const [page, setPage] = useState(1);

  const getListVoucherRedemptionQuery = useQuery(
    getListVoucherRedemptionQueryOptions({
      voucher_id: voucherId,
      page,
      limit: LIMIT,
    })
  );

  const redemptions = getListVoucherRedemptionQuery.data?.result.data ?? [];
  const totalData = getListVoucherRedemptionQuery.data?.result.total_data ?? 0;
  const lastPage = getListVoucherRedemptionQuery.data?.result.last_page ?? 1;

  if (getListVoucherRedemptionQuery.isPending) {
    return <VoucherRedemptionHistorySkeleton />;
  }

  if (getListVoucherRedemptionQuery.isError) {
    return (
      <p className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Gagal memuat riwayat voucher. Silakan coba lagi.
      </p>
    );
  }

  if (redemptions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
        <ReceiptIcon className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          Belum ada riwayat penggunaan
        </p>
        <p className="text-xs text-muted-foreground">
          Voucher ini belum pernah dipakai.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        {totalData} penggunaan tercatat
      </p>

      <div className="flex max-h-80 flex-col gap-2 overflow-y-auto pr-1">
        {redemptions.map(redemption => (
          <VoucherRedemptionItem key={redemption.id} redemption={redemption} />
        ))}
      </div>

      {lastPage > 1 && (
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <span className="text-xs text-muted-foreground">
            Halaman {page} dari {lastPage}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(currentPage => currentPage - 1)}
            >
              Sebelumnya
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= lastPage}
              onClick={() => setPage(currentPage => currentPage + 1)}
            >
              Berikutnya
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface VoucherRedemptionItemProps {
  redemption: VoucherRedemption;
}

function VoucherRedemptionItem({ redemption }: VoucherRedemptionItemProps) {
  const status = STATUS_LABEL[redemption.status.toUpperCase()];
  const productNames = redemption.order?.items
    .map(orderItem => orderItem.product?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-background text-muted-foreground">
        <UserIcon className="size-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-sm font-medium text-foreground">
          {redemption.user_email ?? 'User tidak diketahui'}
        </p>
        <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
          <PackageIcon className="size-3 shrink-0" />
          {productNames || '-'}
        </p>
        {status && (
          <Badge variant={status.variant} className="w-fit">
            {status.label}
          </Badge>
        )}
      </div>

      <p className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
        <CalendarIcon className="size-3" />
        {formatDate(redemption.consumed_at ?? redemption.reserved_at)}
      </p>
    </div>
  );
}

function VoucherRedemptionHistorySkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-[62px] w-full rounded-lg" />
      ))}
    </div>
  );
}
